import CryptoJS from "crypto-js";

const DECRYPTION_KEY = "immuno-act-360";

function hasControlCharacters(str) {
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    // Exclude common whitespaces: tab (9), line feed (10), carriage return (13)
    if ((code < 32 && code !== 9 && code !== 10 && code !== 13) || (code >= 127 && code <= 159)) {
      return true;
    }
  }
  return false;
}

export function decryptVal(value, visited = new WeakSet()) {
  if (typeof value !== "string") return value;

  let trimmed = value.trim();
  
  // Attempt URL decoding first
  try {
    if (trimmed.includes("%")) {
      trimmed = decodeURIComponent(trimmed);
    }
  } catch (e) {
    // Ignore URL decoding errors and proceed
  }

  if (trimmed.length < 4) return value;

  // 1. Check if the string itself is a JSON string (unencrypted but containing JSON array or object)
  if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
    try {
      const parsed = JSON.parse(trimmed);
      const decryptedParsed = decryptData(parsed, visited);
      return JSON.stringify(decryptedParsed);
    } catch (e) {
      // Not valid JSON, proceed to standard decryption
    }
  }

  // Handle double-serialized JSON strings (e.g. "\"[{...}]\"")
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      const unescaped = JSON.parse(trimmed);
      if (typeof unescaped === "string" && ((unescaped.startsWith("{") && unescaped.endsWith("}")) || (unescaped.startsWith("[") && unescaped.endsWith("]")))) {
        const parsed = JSON.parse(unescaped);
        const decryptedParsed = decryptData(parsed, visited);
        return JSON.stringify(decryptedParsed);
      }
    } catch (e) {
      // Not valid double-serialized JSON
    }
  }

  // 2. Check for embedded CryptoJS encrypted tokens (starting with U2FsdGVkX1)
  const cryptoRegex = /U2FsdGVkX1(?:(?!U2FsdGVkX1)[A-Za-z0-9+/_-])*={0,2}/g;
  let replaced = trimmed;
  let hasDecryptedSub = false;

  try {
    replaced = trimmed.replace(cryptoRegex, (match) => {
      try {
        let standardBase64 = match;
        if (match.includes("-") || match.includes("_")) {
          standardBase64 = match.replace(/-/g, "+").replace(/_/g, "/");
        }

        const bytes = CryptoJS.AES.decrypt(standardBase64, DECRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);

        if (decrypted && decrypted.length > 0 && !hasControlCharacters(decrypted)) {
          hasDecryptedSub = true;
          // If the decrypted token is itself a JSON string, decrypt its contents and return as string
          const decTrimmed = decrypted.trim();
          if ((decTrimmed.startsWith("{") && decTrimmed.endsWith("}")) || (decTrimmed.startsWith("[") && decTrimmed.endsWith("]"))) {
            try {
              const parsed = JSON.parse(decTrimmed);
              const decryptedParsed = decryptData(parsed, visited);
              return JSON.stringify(decryptedParsed);
            } catch (e) {
              // return decrypted string
            }
          } else if (decTrimmed.startsWith('"') && decTrimmed.endsWith('"')) {
            try {
              const unescaped = JSON.parse(decTrimmed);
              if (typeof unescaped === "string" && ((unescaped.startsWith("{") && unescaped.endsWith("}")) || (unescaped.startsWith("[") && unescaped.endsWith("]")))) {
                const parsed = JSON.parse(unescaped);
                const decryptedParsed = decryptData(parsed, visited);
                return JSON.stringify(decryptedParsed);
              }
            } catch (e) {
              // return decrypted string
            }
          }
          return decrypted;
        }
      } catch (e) {
        // failed to decrypt match
      }
      return match;
    });
  } catch (e) {
    // replace error
  }

  if (hasDecryptedSub) {
    return replaced;
  }

  // No "U2FsdGVkX1" (CryptoJS/OpenSSL salted-ciphertext) marker found, so this
  // is plain text, not an encrypted value. Do NOT fall back to blindly
  // AES-decrypting anything that merely looks like base64 (i.e. any
  // alphanumeric string) - that produces false-positive "decryptions" that
  // silently corrupt ordinary field values (names, statuses, ids, etc.) into
  // garbled/mojibake text.
  return value;
}

export function decryptData(data, visited = new WeakSet()) {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data === "string") {
    return decryptVal(data, visited);
  }

  if (Array.isArray(data)) {
    if (visited.has(data)) return data;
    visited.add(data);
    return data.map((item) => decryptData(item, visited));
  }

  if (data instanceof Map) {
    if (visited.has(data)) return data;
    visited.add(data);
    const decryptedMap = new Map();
    data.forEach((val, key) => {
      const decryptedKey = decryptData(key, visited);
      const decryptedVal = decryptData(val, visited);
      decryptedMap.set(decryptedKey, decryptedVal);
    });
    return decryptedMap;
  }

  if (data instanceof Set) {
    if (visited.has(data)) return data;
    visited.add(data);
    const decryptedSet = new Set();
    data.forEach((val) => {
      decryptedSet.add(decryptData(val, visited));
    });
    return decryptedSet;
  }

  if (typeof data === "object") {
    if (data instanceof Date || data instanceof RegExp) {
      return data;
    }

    // Check if it is a DOM element or a React element
    if (data.$$typeof || data.nodeType) {
      return data;
    }

    if (visited.has(data)) return data;
    visited.add(data);

    const decryptedObj = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const decryptedKey = decryptVal(key, visited);
        decryptedObj[decryptedKey] = decryptData(data[key], visited);
      }
    }
    return decryptedObj;
  }

  return data;
}
